<template>
  <div class="border-2 border-gray-800">
    
    <LiabilityHeader 
      :index="index" 
      :updateValue="updateValue"
      :liabilityId="liability.id"
      :liabilityType="parseInt(liability.type)"
      :deleteLiability="deleteLiability"></LiabilityHeader>

    <div class="flex flex-wrap -mx-2 p-4">
      <div class="w-full lg:w-1/4 px-2">
        <InputText 
          label="Address"
          propertyKey="address"
          objectType="liability"
          :index="index"
          :objectId="liability.id"
          :value="liability.address"
          :updateValue="updateValue"
          :suggestions="addressSuggestions"
          :required="true" />

        <div class="mt-6">
          <LenderSelector
            label="Lender"
            objectType="liability"
            propertyKey="lender"
            :index="index"
            :objectId="objectId"
            :value="liability.lender"
            :updateValue="updateValue"
            :required="true" />
        </div>

        <div class="mt-6" v-if="liability.lender != null && isOtherLander(liability.lender)">
          <InputText 
            label="Name of lender"
            propertyKey="lender"
            objectType="liability"
            :index="index"
            :objectId="liability.id"
            :value="liability.lender"
            :updateValue="updateValue" />
        </div>

        <div class="mt-6">
          <InputBSB 
            label="BSB"
            propertyKey="bsb"
            objectType="liability"
            :bankProp="liability.bank"
            :index="index"
            :objectId="liability.id"
            :value="liability.bsb"
            :updateValue="updateValue"
            :required="true"></InputBSB>
        </div>

        <div class="mt-6">
          <InputText 
            label="Account number"
            propertyKey="account_number"
            objectType="liability"
            :index="index"
            :objectId="liability.id"
            :value="liability.account_number"
            :updateValue="updateValue"
            :required="true" />
        </div>

        <div class="mt-6">
          <div class="label-standard">Loan type</div>
          <div class="ml-1 mt-1 flex items-center">
            <InputRadio 
              :name="'input-loan-type-' + liability.id"
              :id="'input-loan-type-principal-interest-' + liability.id"
              propertyKey="loan_type"
              objectType="liability"
              value="principal-interest"
              :index="index"
              :objectId="liability.id"
              :checked="liability.loan_type == 'principal-interest'"
              :updateValue="updateValue" />
            
            <label class="text-sm ml-2 mr-8" :for="'input-loan-type-principal-interest-' + liability.id">Principal &amp; interest</label>
            
            <InputRadio 
              :name="'input-loan-type-' + liability.id"
              :id="'input-loan-type-interest-' + liability.id"
              propertyKey="loan_type"
              objectType="liability"
              value="interest"
              :index="index"
              :objectId="liability.id"
              :checked="liability.loan_type == 'interest'"
              :updateValue="updateValue" />

            <label class="text-sm ml-2" :for="'input-loan-type-interest-' + liability.id">Interest only</label>
          </div>
        </div>

        <div class="mt-6">
          <InputPercentage 
            label="Current interest rate"
            propertyKey="current_interest_rate"
            tooltip="What is the interest rate on this loan?"
            :index="index"
            :objectId="liability.id"
            :value="liability.current_interest_rate"
            :updateValue="updateValue"></InputPercentage>
        </div>

        <div class="mt-6" v-if="liability.loan_type != null && liability.loan_type == 'principal-interest'">
          <InputDatePicker 
            label="Fixed rate expiry, if fixed loan"
            propertyKey="loan_expire_at"
            objectType="liability"
            :index="index"
            :objectId="liability.id"
            :value="liability.loan_expire_at"
            :updateValue="updateValue" />
        </div>

        <template v-if="liability.loan_type != null && liability.loan_type == 'interest'">
          <div class="mt-6">
            <InputDatePicker 
              label="Interest only expiry date"
              propertyKey="interest_only_expiry_date"
              objectType="liability"
              :index="index"
              :objectId="liability.id"
              :value="liability.interest_only_expiry_date"
              :updateValue="updateValue" />
          </div>
        </template>

        <div class="mt-6">
          <InputNumber 
            label="Remaining term in years"
            propertyKey="remaining_years"
            objectType="liability"
            :index="index"
            :objectId="liability.id"
            :value="liability.remaining_years"
            :updateValue="updateValue" />
        </div>
      
        <div class="mt-6" v-if="$store.state.view == 'broker'">  
          <InputDatePicker 
            label="Settlement date"
            propertyKey="settlement_date"
            objectType="liability"
            :index="index"
            :objectId="liability.id"
            :value="liability.settlement_date"
            :updateValue="updateValue" />
        </div>
      </div>

      <div class="w-full lg:w-1/2 px-2 lg:px-0">
        <div class="flex flex-wrap">
          <div class="w-full lg:w-1/3 px-0 lg:px-2 mt-6 lg:mt-0">
            <InputDollarValue 
              label="Monthly payment"
              propertyKey="monthly_payment"
              objectType="liability"
              :objectId="liability.id"
              :index="index"
              :value="liability.monthly_payment"
              :updateValue="updateValue"/>
          </div>

          <div class="w-full lg:w-1/3 px-0 lg:px-2 mt-6 lg:mt-0">
            <InputDollarValue 
              label="Limit"
              propertyKey="limit"
              objectType="liability"
              tooltip="What is the maximum limit that you have?"
              :objectId="liability.id"
              :index="index"
              :value="liability.limit"
              :updateValue="updateValue"/>
          </div>

          <div class="w-full lg:w-1/3 px-0 lg:px-2 mt-6 lg:mt-0">
            <InputDollarValue 
              label="Balance"
              propertyKey="balance"
              objectType="liability"
              tooltip="What amount do you currently owe?"
              :objectId="liability.id"
              :index="index"
              :value="liability.balance"
              :updateValue="updateValue"/>
          </div>
        </div>
      </div>

      <div class="w-full lg:w-1/4 px-2 mt-6 lg:mt-0">
        <Ownership :liability="liability" />
      </div>
    </div>
  </div>
</template>

<script>
  import axios              from 'axios'
  import moment             from 'moment'
  import LiabilityHeader    from '../LiabilityHeader'
  import LenderSelector     from '@/shared/components/form/LenderSelector'
  import InputText          from '@/shared/components/form/InputText'
  import InputNumber        from '@/shared/components/form/InputNumber'
  import InputDollarValue   from '@/shared/components/form/InputDollarValue'
  import InputPercentage    from '@/shared/components/form/InputPercentage'
  import InputBSB           from '@/shared/components/form/InputBSB'
  import InputDatePicker    from '@/shared/components/form/InputDatePicker'
  import InputRadio         from '@/shared/components/form/InputRadio'
  import Ownership          from '@/shared/components/Ownership'

  export default {

    components: {
      LenderSelector,
      InputText,
      InputNumber,
      InputDollarValue,
      Ownership,
      InputBSB,
      InputRadio,
      InputDatePicker,
      InputPercentage,
      LiabilityHeader
    },

    props : {
      liability : {
        type : Object,
        required : true
      },

      index : {
        type : Number,
        required : false
      },

      updateValue : {
        type     : Function,
        required : false
      },

      objectId : {
        type : Number,
        required : false
      },

      deleteLiability : {
        type    : Function,
        required: false
      },
    },

    data() {
      return {
        addressSuggestions : null
      }
    },

    created() {
      this.getAddressSuggestions()
    },

    methods : {

      getAddressSuggestions() {
        this.showLoader("Loading addresses..")
        this.apiGet({
          clientId : this.$route.params.clientId
        }, 'address/suggestions', (addresses) => {
          this.hideLoader()
          let arr = []
          for(let i=0; i < addresses.length; i++) {
            arr.push({
              id         : addresses[i],
              name       : addresses[i],
              description: addresses[i],
            })
          }
          this.addressSuggestions = arr          
        })
      },

      // getRemainingTerms(value) {
      //   return moment(value, 'YYYY-MM-DD').diff(moment(), 'years')
      // }
    }
  }
</script>